/** @jsx jsx */
import React, { memo, useCallback, useEffect, useRef } from 'react';
import { jsx } from '@emotion/core';
import { bind } from 'bind-event-listener';
import NodeResolver from 'react-node-resolver';
import { createAndFireEvent, withAnalyticsContext, withAnalyticsEvents } from '@atlaskit/analytics-next';
import noop from '@atlaskit/ds-lib/noop';
import { Manager, Popper, Reference } from '@atlaskit/popper';
import { Container } from './styled/container';
var packageName = "@atlaskit/inline-dialog";
var packageVersion = "13.3.6";

var checkIsChildOfPortal = function checkIsChildOfPortal(node) {
  if (!node) {
    return false;
  }

  return node.classList && node.classList.contains('atlaskit-portal-container') || checkIsChildOfPortal(node.parentElement);
};

var InlineDialog = /*#__PURE__*/memo(function InlineDialog(_ref) {
  var _ref$isOpen = _ref.isOpen,
      isOpen = _ref$isOpen === void 0 ? false : _ref$isOpen,
      _ref$onContentBlur = _ref.onContentBlur,
      onContentBlur = _ref$onContentBlur === void 0 ? noop : _ref$onContentBlur,
      _ref$onContentClick = _ref.onContentClick,
      onContentClick = _ref$onContentClick === void 0 ? noop : _ref$onContentClick,
      _ref$onContentFocus = _ref.onContentFocus,
      onContentFocus = _ref$onContentFocus === void 0 ? noop : _ref$onContentFocus,
      _ref$onClose = _ref.onClose,
      onClose = _ref$onClose === void 0 ? noop : _ref$onClose,
      _ref$placement = _ref.placement,
      placement = _ref$placement === void 0 ? 'bottom-start' : _ref$placement,
      _ref$strategy = _ref.strategy,
      strategy = _ref$strategy === void 0 ? 'fixed' : _ref$strategy,
      testId = _ref.testId,
      content = _ref.content,
      children = _ref.children;
  var containerRef = useRef(null);
  var triggerRef = useRef(null); // we put this into a ref to avoid handleClickOutside having this as a dependency

  var onCloseRef = useRef(onClose);
  useEffect(function () {
    onCloseRef.current = onClose;
  });
  var handleClickOutside = useCallback(function (event) {
    var target = event.target; // checks for when target is not HTMLElement

    if (!(target instanceof HTMLElement)) {
      return;
    } // TODO: This is to handle the case where the target is no longer in the DOM.
    // This happens with react-select in datetime picker. There might be other
    // edge cases for this.


    if (!document.body.contains(target)) {
      return;
    } // exit if we click outside but on the trigger — it can handle the clicks itself


    if (triggerRef.current && triggerRef.current.contains(target)) {
      return;
    } // handles the case where inline dialog opens portalled elements such as modal


    if (checkIsChildOfPortal(target)) {
      return;
    } // call onClose if the click originated from outside the dialog


    if (containerRef.current && !containerRef.current.contains(target)) {
      var _onCloseRef$current;

      (_onCloseRef$current = onCloseRef.current) === null || _onCloseRef$current === void 0 ? void 0 : _onCloseRef$current.call(onCloseRef, {
        isOpen: false,
        event: event
      });
    }
  }, []);
  useEffect(function () {
    if (!isOpen) {
      return;
    }

    var unbind = bind(window, {
      type: 'click',
      listener: handleClickOutside,
      options: {
        capture: true
      }
    });
    return unbind;
  }, [isOpen, handleClickOutside]);
  var popper = isOpen ? jsx(Popper, {
    placement: placement,
    strategy: strategy
  }, function (_ref2) {
    var _ref3 = _ref2.ref,
        style = _ref2.style;
    return jsx(Container, {
      onBlur: onContentBlur,
      onFocus: onContentFocus,
      onClick: onContentClick,
      ref: function ref(node) {
        if (node) {
          containerRef.current = node;

          if (typeof _ref3 === 'function') {
            _ref3(node);
          } else {
            _ref3.current = node;
          }
        }
      },
      style: style,
      testId: testId
    }, content);
  }) : null;
  return jsx(Manager, null, jsx(Reference, null, function (_ref4) {
    var ref = _ref4.ref;
    return jsx(NodeResolver, {
      innerRef: function innerRef(node) {
        triggerRef.current = node;

        if (typeof ref === 'function') {
          ref(node);
        } else {
          ref.current = node;
        }
      }
    }, children);
  }), popper);
});
InlineDialog.displayName = 'InlineDialog';
export { InlineDialog as InlineDialogWithoutAnalytics };
var createAndFireEventOnAtlaskit = createAndFireEvent('atlaskit');
export default withAnalyticsContext({
  componentName: 'inlineDialog',
  packageName: packageName,
  packageVersion: packageVersion
})(withAnalyticsEvents({
  onClose: createAndFireEventOnAtlaskit({
    action: 'closed',
    actionSubject: 'inlineDialog',
    attributes: {
      componentName: 'inlineDialog',
      packageName: packageName,
      packageVersion: packageVersion
    }
  })
})(InlineDialog));